import { Icon } from 'idea-react';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';

import { t } from '../../model/Translation';

interface Step3ProcessingProps {
    selectedFiles: File[];
    uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
    processingFiles: Map<string, number>;
    onPrev: () => void;
}

@observer
export class Step3Processing extends Component<Step3ProcessingProps> {
    renderFileProgress = (file: File) => {
        const { processingFiles } = this.props;
        const progress = processingFiles.get(file.name) || 0;

        return (
            <div key={file.name} className="mb-3 p-3 border rounded bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center">
                        <Icon
                            name="file-earmark-word"
                            className="me-2"
                            style={{
                                fontSize: '20px',
                                color: '#2b579a'
                            }}
                        />
                        <span>{file.name}</span>
                    </div>
                    <span className="fw-bold">{progress}%</span>
                </div>
                <ProgressBar
                    now={progress}
                    variant="primary"
                    style={{ height: '8px' }}
                />
            </div>
        );
    };

    render() {
        const { uploadStatus, selectedFiles, onPrev } = this.props;

        return (
            <div>
                {/* Header */}
                <div className="mb-4">
                    <h3 className="d-flex align-items-center mb-3">
                        <Icon
                            name="emoji-smile"
                            className="me-2"
                            style={{ fontSize: '32px' }}
                        />
                        {t('document_uploaded')}
                    </h3>
                    <p className="text-muted mb-4">{t('upload_to_kb_hint')}</p>
                </div>

                {/* Processing Status */}
                <div className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                        <div
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="fw-medium">
                            {t('embedding_processing')}
                        </span>
                    </div>

                    {/* File Progress */}
                    {uploadStatus === 'uploading' &&
                        selectedFiles.map(this.renderFileProgress)}
                </div>

                {/* Configuration Section */}
                <div className="border rounded p-4 mb-4">
                    <div className="row g-3">
                        {/* Row 1 */}
                        <div className="col-6">
                            <div className="text-muted small">
                                {t('chunking_mode')}
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="fw-medium">{t('custom')}</div>
                        </div>

                        {/* Row 2 */}
                        <div className="col-6">
                            <div className="text-muted small">
                                {t('max_chunk_length')}
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="fw-medium">1024</div>
                        </div>

                        {/* Row 3 */}
                        <div className="col-6">
                            <div className="text-muted small">
                                {t('text_preprocessing')}
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="fw-medium">
                                {t('replace_whitespace')}
                            </div>
                        </div>

                        {/* Row 4 */}
                        <div className="col-6">
                            <div className="text-muted small">
                                {t('index_method')}
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <Icon
                                    name="stars"
                                    className="me-1"
                                    style={{ color: '#ff6b35' }}
                                />
                                <span className="fw-medium">
                                    {t('high_quality')}
                                </span>
                            </div>
                        </div>

                        {/* Row 5 */}
                        <div className="col-6">
                            <div className="text-muted small">
                                {t('retrieval_settings')}
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <Icon
                                    name="grid-3x3-gap"
                                    className="me-1"
                                    style={{ color: '#6366f1' }}
                                />
                                <span className="fw-medium">
                                    {t('hybrid_search')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex justify-content-between mt-4">
                    <Button
                        variant="outline-secondary"
                        style={{ minWidth: '120px' }}
                        disabled={uploadStatus === 'uploading'}
                        onClick={onPrev}
                    >
                        <Icon name="arrow-left" className="me-1" />{' '}
                        {t('previous_step')}
                    </Button>
                    <Button
                        variant="primary"
                        href="#/document"
                        style={{
                            minWidth: '120px',
                            backgroundColor: '#4169E1',
                            borderColor: '#4169E1'
                        }}
                        disabled={uploadStatus === 'uploading'}
                    >
                        {t('complete')}
                    </Button>
                </div>
            </div>
        );
    }
}
